import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { FaTimes } from "react-icons/fa";
import { useBackToClose } from "../../utils/backToClose";

const OUTPUT_SIZE = 512;

// Draws the selected square of the image onto a 512x512 canvas and returns it as a JPEG blob.
// (The server re-encodes it again; cropping here just keeps the upload small and lets the user frame it.)
async function cropToBlob(imageUrl, area) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // transparent PNGs get a white background instead of black
  ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not process the image"))), "image/jpeg", 0.92)
  );
}

function AvatarCropModal({
  file,
  onCancel,
  onSave,
  saving,
  progress,
  error,
  title = "Crop your photo",
  hint = "Drag to reposition. Your photo is visible to everyone in the system.",
}) {
  useBackToClose(true, () => !saving && onCancel());
  const [imageUrl, setImageUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, saving]);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const save = async () => {
    if (!area) return;
    setLocalError("");
    try {
      onSave(await cropToBlob(imageUrl, area));
    } catch (err) {
      setLocalError("This image couldn't be processed. Try a different file.");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4">
      <button type="button" aria-label="Close" onClick={() => !saving && onCancel()} className="absolute inset-0" />
      <div role="dialog" aria-modal="true" aria-labelledby="avatar-crop-title" className="relative w-full max-w-md rounded-2xl border border-app-primary bg-app-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-app-primary px-5 py-4">
          <h3 id="avatar-crop-title" className="text-lg font-semibold text-app-heading">{title}</h3>
          <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg p-2 text-app-muted hover:bg-app-hover hover:text-app-primary disabled:opacity-50" aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="relative h-72 bg-gray-900">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="px-5 py-4 space-y-3">
          <label htmlFor="avatar-zoom" className="block text-sm font-medium text-app-secondary">Zoom</label>
          <input
            id="avatar-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-[#2470B7]"
          />
          <p className="text-xs text-app-muted">{hint}</p>
          {(error || localError) && (
            <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error || localError}</p>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-app-primary px-5 py-4">
          <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary hover:bg-app-hover disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving || !area} className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1f5f9b] disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? `Uploading${progress ? ` ${progress}%` : "…"}` : "Save photo"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AvatarCropModal;
